import React, { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import toast from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";

import Button from "@/components/loading-button";
import api from "@/services/api";

const translations = {
  en: {
    title: "Set your new password",
    description: "Please enter your new password",
    password: "New Password",
    confirm_password: "Confirm Password",
    button: "Reset Password",
    back_to_sign_in: "Back to Sign In",
    success_message: "Password has been reset successfully",
    error: {
      password_required: "Password is required",
      passwords_do_not_match: "Passwords do not match",
      failed: "Failed to reset password",
    },
    password_validation: {
      should_contain: "Your password should contain",
      min_length: "At least 8 characters",
      lowercase: "At least one lowercase letter",
      uppercase: "At least one uppercase letter",
      numbers: "At least one number",
    },
  },
  fr: {
    title: "Définir un nouveau mot de passe",
    description: "Veuillez entrer votre nouveau mot de passe",
    password: "Nouveau mot de passe",
    confirm_password: "Confirmer le mot de passe",
    button: "Réinitialiser le mot de passe",
    back_to_sign_in: "Retour à la connexion",
    success_message: "Le mot de passe a été réinitialisé avec succès",
    error: {
      password_required: "Le mot de passe est requis",
      passwords_do_not_match: "Les mots de passe ne correspondent pas",
      failed: "Échec de la réinitialisation du mot de passe",
    },
    password_validation: {
      should_contain: "Votre mot de passe doit contenir",
      min_length: "Au moins 8 caractères",
      lowercase: "Au moins une lettre minuscule",
      uppercase: "Au moins une lettre majuscule",
      numbers: "Au moins un chiffre",
    },
  },
};

export default function Reset() {
  const { t } = useComponentTranslation(translations);
  const navigate = useNavigate();
  const [seePassword, setSeePassword] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [values, setValues] = useState({
    password: "",
    // confirm_password: "",
  });

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!values.password) return toast.error(t("error.password_required"));

    setBtnLoading(true);
    try {
      const res = await api.post("/user/forgot_password_reset", { ...values, token });
      if (!res.ok) throw res;

      toast.success(t("success_message"));
      navigate("/auth/signin");
    } catch (e) {
      toast.error(e.code);
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      {/* Main Card */}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-gray-200 p-4">
        <div className="text-center mb-8">
          <div className="mb-8 flex justify-center">
            <span className="text-xl font-extrabold text-gray-900">Jobego</span>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">{t("title")}</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Password */}
          <div>
            <label htmlFor="password" className="input-label">
              {t("password")}
            </label>
            <div className="relative">
              <input
                type={seePassword ? "text" : "password"}
                id="password"
                name="password"
                value={values.password}
                onChange={(e) => setValues({ ...values, password: e.target.value })}
                className="input pr-10"
                placeholder="••••••••"
                required
              />
              <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600" onClick={() => setSeePassword(!seePassword)}>
                {seePassword ? <BsEyeSlash className="h-5 w-5" /> : <BsEye className="h-5 w-5" />}
              </button>
            </div>

            {/* Password Validation */}
            <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">{t("password_validation.should_contain")}:</p>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <svg
                    className={`h-4 w-4 mr-2 shrink-0 ${values.password.length >= 8 ? "text-green-600" : "text-gray-400"}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {values.password.length >= 8 ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    )}
                  </svg>
                  <span className={values.password.length >= 8 ? "text-gray-900 font-medium" : "text-gray-600"}>{t("password_validation.min_length")}</span>
                </div>
                <div className="flex items-center text-sm">
                  <svg
                    className={`h-4 w-4 mr-2 shrink-0 ${/[a-z]/.test(values.password) ? "text-green-600" : "text-gray-400"}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {/[a-z]/.test(values.password) ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    )}
                  </svg>
                  <span className={/[a-z]/.test(values.password) ? "text-gray-900 font-medium" : "text-gray-600"}>{t("password_validation.lowercase")}</span>
                </div>
                <div className="flex items-center text-sm">
                  <svg
                    className={`h-4 w-4 mr-2 shrink-0 ${/[A-Z]/.test(values.password) ? "text-green-600" : "text-gray-400"}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {/[A-Z]/.test(values.password) ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    )}
                  </svg>
                  <span className={/[A-Z]/.test(values.password) ? "text-gray-900 font-medium" : "text-gray-600"}>{t("password_validation.uppercase")}</span>
                </div>
                <div className="flex items-center text-sm">
                  <svg
                    className={`h-4 w-4 mr-2 shrink-0 ${/[0-9]/.test(values.password) ? "text-green-600" : "text-gray-400"}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {/[0-9]/.test(values.password) ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    )}
                  </svg>
                  <span className={/[0-9]/.test(values.password) ? "text-gray-900 font-medium" : "text-gray-600"}>{t("password_validation.numbers")}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Confirm Password */}
          {/* <div>
            <label htmlFor="confirm_password" className="input-label">
              {t("confirm_password")}
            </label>
            <input
              type={seePassword ? "text" : "password"}
              id="confirm_password"
              name="confirm_password"
              value={values.confirm_password}
              onChange={(e) => setValues({ ...values, confirm_password: e.target.value })}
              className="input"
              placeholder="••••••••"
              required
            />
          </div> */}

          {/* Submit Button */}
          <Button loading={btnLoading} className="btn btn-primary w-full btn-large" disabled={!values.password}>
            {t("button")}
          </Button>

          <div className="text-center">
            <Link to="/auth/signin" className="text-sm text-[#6C4CFF] font-medium hover:underline">
              {t("back_to_sign_in")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
